import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const objects = [
    { name: "Pencil", long: "pencil-long.png", short: "pencil-short.png" },
    { name: "Ruler", long: "ruler-long.png", short: "ruler-short.png" },
    { name: "Snake", long: "snake-long.png", short: "snake-short.png" },
    { name: "Ladder", long: "ladder-long.png", short: "ladder-short.png" },
    { name: "Bridge", long: "bridge-long.png", short: "bridge-short.png" },
];

const LongShort: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [target, setTarget] = useState("long");
    const [selectedObject, setSelectedObject] = useState(objects[0]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Long and Short – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const randomObject = objects[Math.floor(Math.random() * objects.length)];
        const randomTarget = Math.random() > 0.5 ? "long" : "short";

        setSelectedObject(randomObject);
        setTarget(randomTarget);
        setQuestion(`Which one is ${randomTarget}?`);
        setMessage("");
    };

    const checkAnswer = (choice: string) => {
        if (choice === target) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach your LKG child the concept of long and short with interactive activities on Mama’s Guidance—designed to build early measurement and comparison skills through fun, visual-based learning.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Long and Short</h1>

                                    <p>
                                        Understanding <strong>length comparison</strong> is one of the earliest math skills for young learners. In this <strong>Long and Short</strong> worksheet, your child will learn to identify and compare objects based on length — a key pre-math concept taught at the LKG level.
                                    </p>

                                    <h2>📏 What Does Long and Short Mean?</h2>
                                    <p>
                                        <strong>“Long“</strong> describes something that extends more in length, while <strong>“short“</strong> means less in length. Learning this contrast helps children develop <strong>observation, vocabulary, and sorting abilities</strong> — all of which are essential for further math learning.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Pictures of everyday objects in different lengths</li>
                                        <li>Circle the longer or shorter object</li>
                                        <li>Match long items with short items</li>
                                        <li>Simple coloring activities to identify lengths</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand the concept of long vs. short</li>
                                        <li>Visually compare lengths of objects</li>
                                        <li>Develop vocabulary related to size and measurement</li>
                                        <li>Improve attention to detail and classification skills</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Point to the pictures and ask your child, <strong>“Which one is long?” or “Which one is short?”</strong></li>
                                        <li>Encourage them to circle or color the correct answers.</li>
                                        <li>Use real-life objects like pencils, ribbons, or sticks to reinforce the concept at home.</li>
                                        <li>Practice using the words <strong>“long”</strong> and <strong>“short”</strong> in daily conversations.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        Comparing lengths develops early <strong>measurement and observation skills</strong>. It lays the foundation for understanding <strong>distance, height, and units of measure</strong> in later grades.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Long and Short</h1>

                                <p>
                                    Understanding <strong>length comparison</strong> is one of the earliest math skills for young learners. In this <strong>Long and Short</strong> worksheet, your child will learn to identify and compare objects based on length — a key pre-math concept taught at the LKG level.
                                </p>

                                <h2>📏 What Does Long and Short Mean?</h2>
                                <p>
                                    <strong>“Long“</strong> describes something that extends more in length, while <strong>“short“</strong> means less in length. Learning this contrast helps children develop <strong>observation, vocabulary, and sorting abilities</strong> — all of which are essential for further math learning.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Pictures of everyday objects in different lengths</li>
                                    <li>Circle the longer or shorter object</li>
                                    <li>Match long items with short items</li>
                                    <li>Simple coloring activities to identify lengths</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the concept of long vs. short</li>
                                    <li>Visually compare lengths of objects</li>
                                    <li>Develop vocabulary related to size and measurement</li>
                                    <li>Improve attention to detail and classification skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Point to the pictures and ask your child, <strong>“Which one is long?” or “Which one is short?”</strong></li>
                                    <li>Encourage them to circle or color the correct answers.</li>
                                    <li>Use real-life objects like pencils, ribbons, or sticks to reinforce the concept at home.</li>
                                    <li>Practice using the words <strong>“long”</strong> and <strong>“short”</strong> in daily conversations.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    Comparing lengths develops early <strong>measurement and observation skills</strong>. It lays the foundation for understanding distance, height, and units of measure in later grades.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-5 mb-5" style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                <img src={process.env.REACT_APP_API_URL + selectedObject.long} alt="Long Object" style={{ width: "100px" }} onClick={() => checkAnswer("long")} />
                                <img src={process.env.REACT_APP_API_URL + selectedObject.short} alt="Short Object" style={{ width: "100px" }} onClick={() => checkAnswer("short")} />
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default LongShort;
